import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Mail, Video, Image as ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, imgUrl } from "@/lib/api";
import { LANGS } from "@/lib/i18n";
import { useReloadSettings } from "@/lib/settings";
import { Card } from "./BookingsTab";
import { Field } from "./ListingTab";

const UploadBtn = ({ label, accept, onDone, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const go = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-media", fd);
      onDone(data.url);
      toast.success("Archivo subido. Recuerda guardar.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" hidden accept={accept} onChange={(e) => go(e.target.files?.[0])} />
      <button type="button" data-testid={testid} disabled={busy} onClick={() => ref.current?.click()} className="flex shrink-0 items-center gap-2 rounded-lg bg-[#2C2016] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><Upload className="h-4 w-4" />{busy ? "Subiendo…" : label}</button>
    </>
  );
};

export const SiteTab = () => {
  const [s, setS] = useState(null);
  const [lang, setLang] = useState("es");
  const [emails, setEmails] = useState("");
  const reloadSite = useReloadSettings();
  useEffect(() => {
    api.get("/admin/settings").then((r) => { setS(r.data); setEmails((r.data.notify_emails || []).join(", ")); });
  }, []);
  if (!s) return null;
  const hero = s.hero || {};
  const setHero = (k, v) => setS({ ...s, hero: { ...hero, [k]: v } });
  const setText = (k, v) => setS({ ...s, hero_text: { ...s.hero_text, [lang]: { ...(s.hero_text?.[lang] || {}), [k]: v } } });
  const save = async () => {
    try {
      const { theme, logo, paypal, ...rest } = s;
      const body = { ...rest, notify_emails: emails.split(/[,\s]+/).filter(Boolean) };
      const { data } = await api.put("/admin/settings", body);
      setS(data);
      setEmails((data.notify_emails || []).join(", "));
      reloadSite();
      toast.success("Sitio actualizado");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const testMail = async () => {
    try {
      const { data } = await api.post("/admin/test-email");
      toast.success(`Correo de prueba enviado a ${data.sent.join(", ")}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-12" data-testid="admin-site">
      <div className="space-y-6 lg:col-span-7">
        <Card className="space-y-4">
          <p className="font-display text-2xl">Portada (video o foto)</p>
          <div className="flex gap-2">
            {[["image", "Foto", ImageIcon], ["video", "Video", Video]].map(([v, l, I]) => (
              <button key={v} data-testid={`hero-type-${v}`} onClick={() => setHero("type", v)} className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm ${hero.type === v ? "bg-[#2C2016] text-white" : "bg-[#F3EFE6]"}`}><I className="h-4 w-4" />{l}</button>
            ))}
          </div>
          {hero.type === "video" ? (
            <>
              <Field label="Video principal (computadora, alta calidad · MP4 hasta 250 MB)">
                <div className="flex gap-2"><Input data-testid="hero-video-input" placeholder="URL .mp4 o sube un archivo" value={hero.video || ""} onChange={(e) => setHero("video", e.target.value)} /><UploadBtn testid="hero-video-upload" label="Subir" accept="video/mp4,video/webm,video/quicktime" onDone={(u) => setHero("video", u)} /></div>
              </Field>
              <Field label="Video para celulares (más liviano, opcional)">
                <div className="flex gap-2"><Input data-testid="hero-video-mobile-input" placeholder="URL .mp4 o sube un archivo" value={hero.video_mobile || ""} onChange={(e) => setHero("video_mobile", e.target.value)} /><UploadBtn testid="hero-video-mobile-upload" label="Subir" accept="video/mp4,video/webm,video/quicktime" onDone={(u) => setHero("video_mobile", u)} /></div>
              </Field>
              <Field label="Imagen mientras carga el video (poster)">
                <div className="flex gap-2"><Input value={hero.poster || ""} onChange={(e) => setHero("poster", e.target.value)} /><UploadBtn label="Subir" accept="image/jpeg,image/png,image/webp" onDone={(u) => setHero("poster", u)} /></div>
              </Field>
              <p className="text-xs text-[#4A3525]">Consejo de calidad: 1920×1080, H.264, 15–40 s, sin audio, 8–20 MB para computadora y 720p (3–8 MB) para celulares.</p>
            </>
          ) : (
            <Field label="Foto de portada (vacío = primera foto de la galería)">
              <div className="flex gap-2"><Input data-testid="hero-image-input" value={hero.image || ""} onChange={(e) => setHero("image", e.target.value)} /><UploadBtn testid="hero-image-upload" label="Subir" accept="image/jpeg,image/png,image/webp" onDone={(u) => setHero("image", u)} /></div>
            </Field>
          )}
          <Field label={`Oscurecer para leer el texto: ${Math.round((hero.overlay ?? 0.35) * 100)}%`}>
            <Slider data-testid="hero-overlay-slider" min={0} max={80} step={5} value={[Math.round((hero.overlay ?? 0.35) * 100)]} onValueChange={([v]) => setHero("overlay", v / 100)} />
          </Field>
          <div className="relative aspect-video overflow-hidden rounded-xl bg-black">
            {hero.type === "video" && hero.video ? <video key={hero.video} src={imgUrl(hero.video)} className="h-full w-full object-cover" autoPlay muted loop playsInline /> : <img src={imgUrl(hero.image || "/images/p16.jpg")} alt="" className="h-full w-full object-cover" />}
            <div className="absolute inset-0 bg-black" style={{ opacity: hero.overlay ?? 0.35 }} />
            <p className="absolute inset-0 flex items-center justify-center px-6 text-center font-display text-2xl uppercase text-white sm:text-4xl">{(s.hero_text?.[lang]?.lines || "Vista previa del texto").split("\n")[0]}</p>
          </div>
        </Card>
        <Card className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="font-display text-2xl">Texto sobre la portada</p>
            <div className="flex gap-1 rounded-full bg-[#F3EFE6] p-1">
              {LANGS.map((l) => <button key={l.code} onClick={() => setLang(l.code)} className={`rounded-full px-3 py-1 text-xs font-semibold ${lang === l.code ? "bg-[#2C2016] text-white" : ""}`}>{l.short}</button>)}
            </div>
          </div>
          <Field label={`Línea pequeña superior (${lang.toUpperCase()}) — si dejas otros idiomas vacíos, se usa el texto en español`}><Input data-testid="hero-overline-input" value={s.hero_text?.[lang]?.overline || ""} onChange={(e) => setText("overline", e.target.value)} /></Field>
          <Field label={`Título grande, una línea por renglón (${lang.toUpperCase()}) — vacío = texto por defecto`}><Textarea data-testid="hero-lines-input" className="min-h-[110px]" value={s.hero_text?.[lang]?.lines || ""} onChange={(e) => setText("lines", e.target.value)} /></Field>
        </Card>
      </div>
      <div className="space-y-6 lg:col-span-5">
        <Card className="space-y-3">
          <p className="font-display text-2xl">Marca y dominio</p>
          <Field label="Nombre del sitio"><Input value={s.brand || ""} onChange={(e) => setS({ ...s, brand: e.target.value })} /></Field>
          <Field label="Dominio"><Input value={s.domain || ""} onChange={(e) => setS({ ...s, domain: e.target.value })} /></Field>
        </Card>
        <Card className="space-y-3">
          <p className="font-display text-2xl">Avisos por correo</p>
          <Field label="Correos que reciben cada nueva reserva (separados por coma, máx. 5)"><Input data-testid="notify-emails-input" placeholder="tu@correo.com, socio@correo.com" value={emails} onChange={(e) => setEmails(e.target.value)} /></Field>
          <button data-testid="test-email-button" onClick={testMail} className="flex items-center gap-2 rounded-lg border border-[#2C2016]/20 px-4 py-2 text-sm font-semibold"><Mail className="h-4 w-4" />Enviar correo de prueba</button>
          <p className="text-xs text-[#4A3525]">Guarda primero los correos. El huésped también recibe una confirmación automática.</p>
        </Card>
        <Card className="space-y-3">
          <p className="font-display text-2xl">Reseñas de huéspedes</p>
          <label className="flex items-center justify-between gap-3 text-sm">Publicar sin revisión previa <Switch data-testid="reviews-autopublish-switch" checked={!!s.reviews_auto_publish} onCheckedChange={(v) => setS({ ...s, reviews_auto_publish: v })} /></label>
        </Card>
        <Card className="space-y-2">
          <p className="font-display text-2xl">Pagos con PayPal</p>
          <p data-testid="paypal-status" className={`text-sm font-semibold ${s.paypal?.enabled ? "text-[#264639]" : "text-[#B8532F]"}`}>{s.paypal?.enabled ? `Activo (${s.paypal.mode === "live" ? "real" : "prueba"})` : "Inactivo: faltan las credenciales de PayPal"}</p>
        </Card>
        <button data-testid="site-save-button" onClick={save} className="w-full rounded-xl bg-[#B8532F] py-4 font-semibold text-white">Guardar sitio web</button>
      </div>
    </div>
  );
};
